import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type NotificationPayload = {
  waste_event_id?: string;
  comment_id?: string;
  actor_user_id?: string;
  actor_name?: string;
  snippet?: string;
};

export type NotificationItem = {
  id: string;
  type: string;
  payload: NotificationPayload;
  read_at: string | null;
  created_at: string;
};

export type NotificationsResult = {
  items: NotificationItem[];
  unreadCount: number;
};

/** The caller's 10 most recent notifications + total unread count (POS2-761). */
export function useNotifications() {
  const { t } = useTranslation();
  const headers = useApiHeaders();

  return useQuery<NotificationsResult>({
    queryKey: ["notifications"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/notifications?limit=10`, { headers });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: NotificationItem[] };
      const unreadCount = Number.parseInt(res.headers.get("X-Unread-Count") ?? "0", 10) || 0;
      return { items: body.data, unreadCount };
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
    // Poll so the badge stays live without a manual refresh.
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_URL}/api/v1/notifications/${id}/read`, {
        method: "PATCH",
        headers,
      });
      if (!res.ok) throw await apiError(t, res);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useMarkAllNotificationsRead() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await fetch(`${API_URL}/api/v1/notifications/read-all`, {
        method: "POST",
        headers,
      });
      if (!res.ok) throw await apiError(t, res);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export type NotificationPreferences = {
  email_digest_enabled: boolean;
};

/** The caller's notification preferences (POS2-762). Defaults to enabled. */
export function useNotificationPreferences() {
  const { t } = useTranslation();
  const headers = useApiHeaders();

  return useQuery<NotificationPreferences>({
    queryKey: ["notification-preferences"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/notifications/preferences`, { headers });
      if (!res.ok) throw await apiError(t, res);
      const body = (await res.json()) as { data: NotificationPreferences };
      return body.data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });
}

export function useUpdateNotificationPreferences() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (prefs: NotificationPreferences): Promise<NotificationPreferences> => {
      const res = await fetch(`${API_URL}/api/v1/notifications/preferences`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(prefs),
      });
      if (!res.ok) throw await apiError(t, res);
      const body = (await res.json()) as { data: NotificationPreferences };
      return body.data;
    },
    onSuccess: (data) => queryClient.setQueryData(["notification-preferences"], data),
  });
}
